// Vorschläge im Warenkorb-Drawer (snippets/fk-cart-upsell.liquid): holt Empfehlungen zum zuletzt hinzugefügten
// Artikel über Shopifys Product Recommendations API und zeigt kleine Karten mit Ein-Klick-Kauf (<fk-add-button>,
// assets/fk-quick-add.js). Nach jeder Warenkorb-Änderung rendert Horizon den Drawer neu – das Element wird dann
// erneut verbunden und lädt passend zum neuen Stand.

const CACHE_TTL = 10 * 60 * 1000;

// Shopify-Geldformate ({{amount}}, {{amount_with_comma_separator}} …) für Beträge in Cent
const money = (cents, format) => {
  const value = Math.round(Number(cents) || 0);
  const fmt = format || '{{amount}}';
  const pattern = /\{\{\s*(\w+)\s*\}\}/;
  const type = (fmt.match(pattern) || [])[1] || 'amount';
  const group = (decimals, thousands, decimal) => {
    const [int, frac] = (Math.abs(value) / 100).toFixed(decimals).split('.');
    return `${value < 0 ? '-' : ''}${int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands)}${frac ? decimal + frac : ''}`;
  };
  const amounts = {
    amount: () => group(2, ',', '.'),
    amount_no_decimals: () => group(0, ',', '.'),
    amount_with_comma_separator: () => group(2, '.', ','),
    amount_no_decimals_with_comma_separator: () => group(0, '.', ','),
    amount_with_space_separator: () => group(2, ' ', ','),
    amount_no_decimals_with_space_separator: () => group(0, ' ', ''),
    amount_with_apostrophe_separator: () => group(2, "'", '.'),
    amount_with_period_and_space_separator: () => group(2, ' ', '.'),
  };
  return fmt.replace(pattern, (amounts[type] || amounts.amount)());
};

const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

class FkCartUpsell extends HTMLElement {
  connectedCallback() {
    this.render();
    // Nach einer Warenkorb-Änderung gleicht Horizon den Drawer mit dem Server-HTML ab (leere Liste, hidden,
    // neue data-exclude) – dann neu rendern. Eigene Änderungen werden über das Flag ausgeblendet.
    this.observer = new MutationObserver(() => {
      if (this.rendering) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.render(), 120);
    });
    this.observer.observe(this, { attributes: true, attributeFilter: ['data-exclude', 'data-product-id', 'hidden'], childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    clearTimeout(this.timer);
  }

  async render() {
    this.list = this.querySelector('[data-list]');
    const productId = this.dataset.productId;
    if (!productId || !this.list) return;
    const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean).map(Number));
    const limit = Number(this.dataset.limit) || 6;
    const products = (await this.fetchRecommendations(productId)).filter((p) => !exclude.has(p.id) && p.available && String(p.id) !== '16012832866634').slice(0, limit);
    const key = products.map((p) => p.id).join(',');
    if (key === this.renderedKey && this.list.childElementCount) return;
    this.rendering = true;
    this.renderedKey = key;
    this.list.innerHTML = products.map((p) => this.card(p)).join('');
    this.hidden = products.length === 0;
    // Mutationen dieses Renders ignorieren
    setTimeout(() => { this.rendering = false; }, 0);
  }

  async fetchRecommendations(productId) {
    const root = window.Shopify?.routes?.root || '/';
    const key = `fk-upsell:${root}:${productId}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.t < CACHE_TTL) return cached.products;
    } catch { /* ohne Cache */ }
    try {
      const base = (window.Theme?.routes?.product_recommendations_url) || `${root}recommendations/products`;
      const res = await fetch(`${base}.json?product_id=${productId}&limit=10&intent=related`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return [];
      const data = await res.json();
      const products = (data.products || []).map((p) => ({
        id: p.id,
        handle: p.handle,
        title: p.title,
        vendor: p.vendor,
        url: p.url,
        available: p.available,
        image: p.featured_image,
        price: p.price,
        variantId: p.variants?.length === 1 ? p.variants[0].id : null,
      }));
      try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), products })); } catch { /* ignorieren */ }
      return products;
    } catch {
      return [];
    }
  }

  card(p) {
    // Marke am Titelanfang weglassen („Stanley Rogers Ceramabake …“), die Karte hat keine Markenzeile
    if (p.vendor && p.title.toLowerCase().startsWith(p.vendor.toLowerCase())) {
      const rest = p.title.slice(p.vendor.length).trim();
      if (rest) p = { ...p, title: rest };
    }
    const img = p.image ? `${p.image}${p.image.includes('?') ? '&' : '?'}width=160` : '';
    const add = p.variantId
      ? `<fk-add-button data-product-id="${p.id}"><form method="post" action="${window.Shopify?.routes?.root || '/'}cart/add" class="fk-cup__form"><input type="hidden" name="id" value="${p.variantId}"><input type="hidden" name="quantity" value="1"><button type="submit" class="fk-cup__add" aria-label="${escapeHtml(this.dataset.addLabel || 'Add')}: ${escapeHtml(p.title)}"><span class="fk-cup__plus" aria-hidden="true"></span><span class="fk-visually-hidden fk-pcard__status" role="status"></span></button></form></fk-add-button>`
      : `<a class="fk-cup__add fk-cup__add--link" href="${p.url}" aria-label="${escapeHtml(p.title)}"><span class="fk-cup__plus" aria-hidden="true"></span></a>`;
    return `<li class="fk-cup__item">
      <a class="fk-cup__media" href="${p.url}" tabindex="-1" aria-hidden="true">${img ? `<img src="${img}" alt="" loading="lazy" width="80" height="80">` : ''}</a>
      <a class="fk-cup__name" href="${p.url}">${escapeHtml(p.title)}</a>
      <div class="fk-cup__row"><span class="fk-cup__price">${money(p.price, this.dataset.moneyFormat)}</span>${add}</div>
    </li>`;
  }
}

if (!customElements.get('fk-cart-upsell')) customElements.define('fk-cart-upsell', FkCartUpsell);
